import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { useSelector } from 'react-redux';

import { FormGroupControl, FormGroupCheck } from '@components/Base';
import { selectCurrentUser, selectIsCustomer, selectIsFrontline } from '@components/Auth';

import { GenderFormSelect } from '../gender-form-select';
import { UserFormValues } from './type';
import { useIsDisabled } from '@hooks/formik';

const GROUP_PROPS = { className: 'mb-3' };

type Props = {
  disabled?: Partial<Record<keyof UserFormValues, boolean>>;
  required: Partial<Record<keyof UserFormValues, boolean>>;
}

const UserFormControl = ({ disabled: propDisabled, required }: Props) => {
  const { t } = useTranslation();
  const currentUser = useSelector(selectCurrentUser);
  const isCustomer = useSelector(selectIsCustomer);
  const isFrontline = useSelector(selectIsFrontline);
  const { initialValues } = useFormikContext<UserFormValues>();
  const disabled = useIsDisabled(initialValues, isCustomer, { mobileNumber: !!initialValues.mobileNumber, ...propDisabled });

  if (!currentUser) return null;

  return (
    <>
      <FormGroupControl
        name="name"
        label={t('user.name')}
        required={required.name}
        disabled={disabled.name}
        groupProps={GROUP_PROPS}
      />

      <FormGroupControl
        name="nameKm"
        label={t('user.nameKm')}
        required={required.nameKm}
        disabled={disabled.nameKm}
        groupProps={GROUP_PROPS}
      />

      <GenderFormSelect
        name="gender"
        required={required.gender}
        disabled={disabled.gender}
        groupProps={GROUP_PROPS}
      />
      <FormGroupControl
        name="personalCode"
        label={t('user.personalCode')}
        required={required.personalCode}
        disabled={disabled.personalCode}
        groupProps={GROUP_PROPS}
      />
      <FormGroupControl
        type="date"
        name="dob"
        label={t('user.dob')}
        required={required.dob}
        disabled={disabled.dob}
        groupProps={GROUP_PROPS}
      />
      <FormGroupControl
        name="occupation"
        label={t('user.occupation')}
        required={required.occupation}
        disabled={disabled.occupation}
        groupProps={GROUP_PROPS}
      />
      <FormGroupControl
        name="email"
        label={t('user.email')}
        required={required.email}
        disabled={disabled.email}
        groupProps={GROUP_PROPS}
      />

      {isFrontline && (
        <FormGroupCheck
          name="noMobileNumber"
          label={t('user.noMobileNumber')}
          required={required.noMobileNumber}
          disabled={disabled.noMobileNumber}
          groupProps={GROUP_PROPS}
        // checked={initialValues.noMobileNumber}
        />
      )}

      <FormGroupControl
        name="mobileNumber"
        label={isFrontline ? t('user.mobileNumberOrUsername') : t('user.mobileNumber')}
        required={required.mobileNumber}
        disabled={disabled.mobileNumber}
        groupProps={GROUP_PROPS}
      />
      <FormGroupControl
        name="address"
        label={t('user.address')}
        required={required.address}
        disabled={disabled.address}
        as="textarea"
        rows={2}
        groupProps={GROUP_PROPS}
      />
    </>
  );
};

export { UserFormControl };
